import * as React from 'react';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { theme } from '../../../../../common/styles/theme';
import { COACH_BOT_URL } from '../../../../../common/constants';
import { useIsMobileLayout } from '../../../../../common/hooks/useIsMobileLayout';
import { LogoText } from '../../../../../../icons/LogoText';
import { HelpIcon } from '../../../../../../icons/HelpIcon';
import { AccountButtonHOC } from '../../AccountButton/hocs/AccountButtonHOC';
import { AppSwitcher } from '../../../shared/AppSwitcher/AppSwitcher';
import { IHeaderProps } from './types';

export const Header: React.FC<IHeaderProps> = ({
	onClickLogo,
	children,
}) => {

	const isMobileLayout = useIsMobileLayout();

	// on the home page the logo leads nowhere, so it scrolls the page body (the header's sibling) up
	const onLogo = (e: React.MouseEvent<HTMLElement>) => {
		e.currentTarget.closest('.header')?.parentElement
			?.querySelector('.page-body')?.scrollTo({ top: 0, behavior: 'smooth' });
		onClickLogo();
	};

	return (
		<Stack
			className={'header'}
			direction={'row'}
			justifyContent={'space-between'}
			alignItems={'center'}
			gap={3}
			sx={{
				flexShrink: 0,
				width: 1,
				height: theme.spacing(7),
				px: theme.spacing(2),
				background: theme.palette.common.white,
				borderBottom: `1px solid ${theme.palette.grey[200]}`,
			}}
		>
			{/* the LangMagic apps menu stands in for the logo's globe; the name after it still leads home */}
			<Stack
				direction={'row'}
				alignItems={'center'}
				sx={{ flexShrink: 0 }}
			>
				<AppSwitcher size={isMobileLayout ? 40 : 32} />

				<Box
					component={'button'}
					aria-label={'Home'}
					onClick={onLogo}
					sx={{
						display: 'flex',
						flexShrink: 0,
						alignItems: 'center',
						gap: theme.spacing(.25),
						p: 0,
						border: 0,
						background: 'none',
						cursor: 'pointer',
					}}
				>
					{!isMobileLayout &&
						<LogoText
							sx={{
								width: '107px', // LogoLabel's lettering at its old scale (136px for the whole logo)
								height: 'auto',
							}}
						/>
					}

					<Box
						component={'span'}
						sx={{
							fontSize: '16px',
							fontWeight: 500,
							lineHeight: 1,
							color: theme.palette.grey[400],
						}}
					>
						meet
					</Box>
				</Box>
			</Stack>

			<Stack
				direction={'row'}
				alignItems={'center'}
				sx={{
					flex: 1,
					minWidth: 0,
				}}
			>
				{children}
			</Stack>

			<Stack
				direction={'row'}
				alignItems={'center'}
				gap={1}
				sx={{ flexShrink: 0 }}
			>
				<Tooltip title={'Help'}>
					<IconButton
						component={'a'}
						href={COACH_BOT_URL}
						target={'_blank'}
						rel={'noopener'}
						aria-label={'Help'}
						sx={{
							width: theme.spacing(5),
							height: theme.spacing(5),
							color: theme.palette.grey[300],
							transition: 'color .3s ease',

							'&:hover': {
								color: theme.palette.grey[500],
							},
						}}
					>
						<HelpIcon sx={{ width: theme.spacing(3), height: theme.spacing(3) }} />
					</IconButton>
				</Tooltip>

				<AccountButtonHOC />
			</Stack>
		</Stack>
	);
};
